import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { ReducersTypes } from "@/redux/reducers/Models";
import { setCustomer, validatePinRegister } from "@/redux/ducks/customer";
import { NavigateFunction } from "react-router-dom";

const pinScheme = z.object({
  pin: z.string().refine((val) => val.length === 5, "Pin inválido"),
});

type State = {
  modalOpen: boolean;
  loading: boolean;
  error: boolean;
};

export default function usePinForm(navigate: NavigateFunction) {
  const dispatch = useDispatch();
  const customer = useSelector((state: ReducersTypes) => state.customerReducer);
  
  const [ui, setUi] = useState<State>({
    modalOpen: false,
    loading: false,
    error: false,
  });

  const unifiedSchema = pinScheme;
  type FormData = z.infer<typeof unifiedSchema>;

  const defaultValues: FormData =  { pin: "" };

  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    getValues,
  } = useForm<FormData>({
    resolver: zodResolver(unifiedSchema),
    mode: "onSubmit",
    defaultValues,
  });

  const toggleModal = useCallback(() => {
    setUi(prev => ({ ...prev, modalOpen: !prev.modalOpen }));
  }, []);

  const handleValidatePin = useCallback((success: boolean, code?: string) => {
    setUi(prev => ({ ...prev, loading: false }));
    if (!success) {
      setUi(prev => ({ ...prev, error: true }));
      if (code === "document") {
        setUi(prev => ({ ...prev, modalOpen: true }));
      }
      return;
    }
    dispatch(
      setCustomer({
        ...customer,
        register: {
          ...customer?.register,
          pin: getValues("pin"),
          hashCode: code,
        }
      })
    );
    navigate('/register/address');
    reset();
  }, [reset, navigate, customer, dispatch, getValues]);

  const onSubmit = useCallback(
    (data: FormData) => {
      if (ui.loading) return;
      setUi(prev => ({ ...prev, loading: true }));
      setUi(prev => ({ ...prev, error: false }));
      dispatch(
        setCustomer({
          ...customer,
          register: {
            ...customer?.register,
            pin: data.pin,
          }
        })
      );
      dispatch(
        validatePinRegister({
          pin: data.pin,
          document: customer?.register?.document!,
          callback: handleValidatePin,
        })
      );
    },
    [ui.loading, dispatch, customer, handleValidatePin]
  );

  return {
    toggleModal,
    isVisibleModal: ui.modalOpen,
    isLoading: ui.loading,
    error: ui.error,
    isDisabled: !isValid || ui.loading,
    register,
    setValue,
    handleSubmit: handleSubmit(onSubmit),
    errors,
  };
} 